import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import { getBlogArticles } from '@/lib/api';

interface BlogArticle {
  id: string;
  title: string;
  preview: string;
  tags: string[];
  created_at: string;
}

const LatestBlogPosts = () => {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLatestArticles();
  }, []);

  const loadLatestArticles = async () => {
    try {
      const data = await getBlogArticles();
      // Get top 3 latest articles
      const latest = (data || []).slice(0, 3);
      setArticles(latest);
    } catch (error) {
      console.error('Error loading latest articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading || articles.length === 0) {
    return null;
  }

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Neueste Blog-Artikel
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              IT-Tipps, Anleitungen und Wissenswertes für Ihren digitalen Alltag
            </p>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {articles.map(article => (
              <Link
                key={article.id}
                to={`/blog/${article.id}`}
                className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="p-6">
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(article.tags || []).slice(0, 2).map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>

                  {/* Date */}
                  <div className="flex items-center text-sm text-slate-500 mb-3">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(article.created_at)}
                  </div>

                  {/* Preview */}
                  <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                    {article.preview}
                  </p>

                  {/* Read More Link */}
                  <div className="flex items-center text-blue-600 font-medium text-sm group-hover:text-blue-700">
                    Weiterlesen
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* View All Button */}
          <div className="text-center">
            <Link
              to="/blog"
              className="inline-flex items-center px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md hover:shadow-lg"
            >
              Alle Artikel anzeigen
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestBlogPosts;
